import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMySubscription } from '../services/api';
import './SubscriptionBanner.css';

function daysUntil(future) {
  if (!future) return 0;
  const ms = new Date(future).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function isPast(d) {
  if (!d) return false;
  return new Date(d).getTime() <= Date.now();
}

const SubscriptionBanner = () => {
  const { t } = useTranslation();
  const [sub, setSub] = useState(null);

  useEffect(() => {
    let active = true;
    getMySubscription()
      .then((res) => {
        if (active) setSub(res.data?.subscription || null);
      })
      .catch(() => {
        // Silent — banner is best-effort.
      });
    return () => { active = false; };
  }, []);

  if (!sub) return null;

  const status = sub.status;
  const trialExpired = status === 'trialing' && isPast(sub.trial_ends_at);
  const activeExpired = status === 'active' && isPast(sub.current_period_end);

  // Active and not flagged for cancellation and not past period end: render nothing.
  if (status === 'active' && !sub.cancel_at_period_end && !activeExpired) return null;

  let tone = 'info';
  let message = null;

  if (trialExpired) {
    tone = 'error';
    message = t('subscription.banner.trialExpired');
  } else if (status === 'trialing') {
    const days = daysUntil(sub.trial_ends_at);
    tone = 'warning';
    message = t('subscription.banner.trial', { count: days });
  } else if (activeExpired) {
    tone = 'error';
    message = t('subscription.banner.expired');
  } else if (status === 'past_due' || status === 'unpaid') {
    tone = 'error';
    message = t('subscription.banner.pastDue');
  } else if (status === 'canceled' || status === 'incomplete_expired') {
    tone = 'error';
    message = t('subscription.banner.canceled');
  } else if (status === 'active' && sub.cancel_at_period_end) {
    tone = 'warning';
    message = t('subscription.banner.cancelAtPeriodEnd');
  } else if (status === 'incomplete') {
    tone = 'warning';
    message = t('subscription.banner.incomplete');
  }

  if (!message) return null;

  return (
    <div className={`sub-banner sub-banner-${tone}`} role="status">
      <span className="sub-banner-text">{message}</span>
      <Link to="/subscription" className="sub-banner-link">
        {t('subscription.banner.cta')}
      </Link>
    </div>
  );
};

export default SubscriptionBanner;
